import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils.js';

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  side?: 'right' | 'left';
  /** Tailwind width class for desktop; full width on phones. */
  width?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Guided-tour anchor on the panel. */
  tour?: string;
}

/** A guided tour (driver.js) is on screen: its popover lives outside the panel. */
const tourActive = (): boolean => typeof document !== 'undefined' && !!document.querySelector('.driver-popover');
/** A click on the tour's popover or dimmed backdrop must not close the panel behind it. */
function keepForTour(e: { target: EventTarget | null; preventDefault: () => void }): void {
  const t = e.target as Element | null;
  if (t?.closest?.('.driver-popover, .driver-overlay') || tourActive()) e.preventDefault();
}

/** Side drawer for detail views and forms (not a blocking pop-up: Esc or the X closes it). */
export function Sheet({ open, onOpenChange, title, description, side = 'right', width = 'sm:max-w-xl', children, footer, tour }: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-[1px] animate-fade-in" />
        <Dialog.Content
          data-tour={tour}
          onPointerDownOutside={keepForTour}
          onInteractOutside={keepForTour}
          onFocusOutside={keepForTour}
          onEscapeKeyDown={(e) => { if (tourActive()) e.preventDefault(); }}
          className={cn(
            'fixed inset-y-0 z-50 flex w-full flex-col bg-card shadow-pop focus:outline-none',
            side === 'right' ? 'right-0 border-l animate-slide-in-right' : 'left-0 border-r animate-slide-in-left',
            width,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-base font-semibold">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-0.5 text-sm text-muted-foreground">{description}</Dialog.Description>
                : <Dialog.Description className="sr-only">Details</Dialog.Description>}
            </div>
            <Dialog.Close className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Close">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t px-5 py-3">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
